export interface DashboardEnrollmentRecord {
  id: string;
  orderId?: string | null;
  isPassEnrollment?: boolean | null;
  status?: string | null;
  refundRequestStatus?: string | null;
  sessionStartDateTime?: string | Date | null;
  sessionEndDateTime?: string | Date | null;
  creatorEmail?: string | null;
  classTitle?: string | null;
}

// In-app cancellation requests stay open until 1 day after the class starts;
// past this the student is directed to email the instructor. Must match
// POST_SESSION_REQUEST_GRACE_MS in shared/cancellation-policy.ts.
export const CANCELLATION_REQUEST_GRACE_MS = 24 * 60 * 60 * 1000;

export interface EnrollmentDashboardGroups<T extends DashboardEnrollmentRecord> {
  upcoming: T[];
  past: T[];
  attention: T[];
}

const ATTENTION_STATUSES = new Set(['refund_pending', 'refunded', 'canceled']);

function toValidDate(date: string | Date | null | undefined): Date | null {
  if (!date) {
    return null;
  }

  const parsed = date instanceof Date ? date : new Date(date);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function compareAscendingBySessionDate<T extends DashboardEnrollmentRecord>(a: T, b: T) {
  const aDate = toValidDate(a.sessionStartDateTime)?.getTime() ?? Number.MAX_SAFE_INTEGER;
  const bDate = toValidDate(b.sessionStartDateTime)?.getTime() ?? Number.MAX_SAFE_INTEGER;
  return aDate - bDate;
}

function compareDescendingBySessionDate<T extends DashboardEnrollmentRecord>(a: T, b: T) {
  return compareAscendingBySessionDate(b, a);
}

export function formatEnrollmentStatusLabel(status: string | null | undefined): string {
  switch (status) {
    case 'refund_pending':
      return 'Refund pending';
    case 'refunded':
      return 'Refunded';
    case 'canceled':
      return 'Canceled';
    case 'confirmed':
      return 'Confirmed';
    default:
      return status ? status.replace(/_/g, ' ') : 'Unknown';
  }
}

export function getEnrollmentDashboardGroups<T extends DashboardEnrollmentRecord>(
  enrollments: T[],
  now: Date = new Date()
): EnrollmentDashboardGroups<T> {
  const groups: EnrollmentDashboardGroups<T> = {
    upcoming: [],
    past: [],
    attention: [],
  };

  for (const enrollment of enrollments) {
    if (ATTENTION_STATUSES.has(enrollment.status ?? '')) {
      groups.attention.push(enrollment);
      continue;
    }

    const sessionStart = toValidDate(enrollment.sessionStartDateTime);
    if (sessionStart && sessionStart.getTime() >= now.getTime()) {
      groups.upcoming.push(enrollment);
      continue;
    }

    groups.past.push(enrollment);
  }

  groups.upcoming.sort(compareAscendingBySessionDate);
  groups.past.sort(compareDescendingBySessionDate);
  groups.attention.sort(compareDescendingBySessionDate);

  return groups;
}

export function canRequestEnrollmentCancellation(
  enrollment: DashboardEnrollmentRecord,
  now: Date = new Date()
): boolean {
  const sessionStart = toValidDate(enrollment.sessionStartDateTime);

  return (
    !!enrollment.orderId &&
    enrollment.status === 'confirmed' &&
    !enrollment.refundRequestStatus &&
    !!sessionStart &&
    // Open until 1 day after the class starts; after that → email the teacher.
    now.getTime() < sessionStart.getTime() + CANCELLATION_REQUEST_GRACE_MS
  );
}

/**
 * Pass-redeemed enrollments have no order, so they use a separate "return the
 * punch" cancellation (not the refund flow). Allowed ahead of time only — before
 * the session starts — and surfaced as its own button.
 */
export function canCancelPassEnrollment(
  enrollment: DashboardEnrollmentRecord,
  now: Date = new Date()
): boolean {
  const sessionStart = toValidDate(enrollment.sessionStartDateTime);

  return (
    enrollment.isPassEnrollment === true &&
    enrollment.status === 'confirmed' &&
    !enrollment.refundRequestStatus &&
    !!sessionStart &&
    sessionStart.getTime() > now.getTime()
  );
}

/**
 * Once the in-app cancellation window has closed (more than 1 day after the
 * class started), a healthy enrollment shows an "email the teacher" option so
 * the student can still raise an issue directly. Mutually exclusive with
 * canRequestEnrollmentCancellation for a given enrollment.
 */
export function shouldEmailTeacherForIssue(
  enrollment: DashboardEnrollmentRecord,
  now: Date = new Date()
): boolean {
  const sessionStart = toValidDate(enrollment.sessionStartDateTime);

  return (
    enrollment.status === 'confirmed' &&
    !enrollment.refundRequestStatus &&
    !!sessionStart &&
    now.getTime() >= sessionStart.getTime() + CANCELLATION_REQUEST_GRACE_MS
  );
}
